import { Loader2 } from 'lucide-react';

import type { AnnouncementDetails } from '~entities/announcements';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';

export interface AnnouncementConfirmAction {
  type: 'publish' | 'archive' | 'delete';
  announcement: AnnouncementDetails;
}

interface AnnouncementActionConfirmDialogProps {
  confirmAction: AnnouncementConfirmAction | null;
  isUpdating: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

function getDialogTitle(type: AnnouncementConfirmAction['type']) {
  switch (type) {
    case 'publish':
      return 'Publish announcement?';
    case 'archive':
      return 'Archive announcement?';
    case 'delete':
      return 'Delete announcement?';
  }
}

function getConfirmLabel(type: AnnouncementConfirmAction['type']) {
  switch (type) {
    case 'publish':
      return 'Publish';
    case 'archive':
      return 'Archive';
    case 'delete':
      return 'Delete';
  }
}

function isDestructiveAction(type: AnnouncementConfirmAction['type']) {
  return type === 'archive' || type === 'delete';
}

function getDialogDescription(action: AnnouncementConfirmAction) {
  const title = action.announcement.title;

  switch (action.type) {
    case 'publish':
      return (
        <>
          &quot;{title}&quot; will go live in the Parent Portal for families to
          see.
        </>
      );
    case 'archive':
      return (
        <>
          &quot;{title}&quot; will be removed from the parent feed. You can
          still view it in the studio as archived.
        </>
      );
    case 'delete':
      return (
        <>
          &quot;{title}&quot; will be permanently deleted. This cannot be undone
          and families will no longer see it in the Parent Portal.
        </>
      );
  }
}

export function AnnouncementActionConfirmDialog({
  confirmAction,
  isUpdating,
  onOpenChange,
  onConfirm,
}: AnnouncementActionConfirmDialogProps) {
  const actionType = confirmAction?.type;

  return (
    <AlertDialog open={confirmAction !== null} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {actionType ? getDialogTitle(actionType) : ''}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {confirmAction ? getDialogDescription(confirmAction) : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isUpdating}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={
              actionType && isDestructiveAction(actionType)
                ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                : undefined
            }
            disabled={isUpdating}
            onClick={(event) => {
              event.preventDefault();
              onConfirm();
            }}
          >
            {isUpdating && <Loader2 className='animate-spin' />}
            {actionType ? getConfirmLabel(actionType) : 'Confirm'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
